package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.payableTimes
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.market.payroll.ShiftPayInput
import com.evoapps.evoshift.domain.market.payroll.WorkedSpan
import com.evoapps.evoshift.domain.model.unpaidBreaks
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.RatesSnapshot
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftCostBreakdown
import com.evoapps.evoshift.domain.model.ShiftFinancials
import com.evoapps.evoshift.domain.model.WorkplaceSettings
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.model.pricedOnServer
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.LocalDateTime

/** The assignee has no pay rates for the shift's workplace. */
class NoPayRatesException(val userId: String, val workplaceId: String) :
    Exception("No pay rates for $userId at $workplaceId")

/**
 * Prices a shift into its cost snapshot (shift_financials) with the payroll rules of the
 * workplace's market: gross pay incl. allowances and the employer's cost, for the planned hours
 * and, once clocked out, for the clocked hours. The person priced is who worked the shift (SH-128:
 * the one recorded afterwards, else the assignee). A shift nobody works has no cost, so its
 * snapshot is removed (it may still hold the previous person's); so is an unpaid owner's.
 */
class CalculateShiftCostUseCase(
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository,
    private val financialRepository: FinancialRepository,
    private val workplaceRepository: WorkplaceRepository,
    /** Pricing needs the pay rates: without MANAGE_PAY it is skipped (a financial manager's refresh completes it). */
    private val caller: CallerCapabilities? = null
) {
    suspend operator fun invoke(shiftId: String): Result<Unit> = price(shiftId).onFailure { e ->
        // Best effort for its callers (assigning, editing, creating), but never silent: a missing
        // snapshot drops the shift from the labour cost until the month is refreshed. Missing pay
        // rates are the owner's to fix (the screens name the people); anything else is a bug.
        if (e !is NoPayRatesException) com.evoapps.evoshift.domain.util.DomainErrors.report(
            com.evoapps.evoshift.domain.util.ShiftPricingFailedException(shiftId, e)
        )
    }

    private suspend fun price(shiftId: String): Result<Unit> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        // SH-267: a borrowed person's shift is priced on the server from their home pay terms.
        if (shift.pricedOnServer) return@runCatching
        val userId = shift.workerId
        val workplaceId = RosterIds.workplaceIdOf(shift.rosterId)
        if (!caller.allows(workplaceId, Capability.MANAGE_PAY)) return@runCatching
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
        // SH-232: paid work on the shift's own day (someone may work without pay from a day on).
        if (userId == null || !workplace.isPaid(userId, shift.date.ifEmpty { shift.plannedTime.start })) {
            financialRepository.deleteShiftFinancials(shiftId).getOrThrow()
            return@runCatching
        }
        val settings = workplace.settings
        // No sensitive data at all is no pay rates (a person added without them), not a failure.
        val sensitive = userRepository.getSensitiveUserData(userId)
        if (sensitive.exceptionOrNull() is com.evoapps.evoshift.domain.model.NotFoundException) throw NoPayRatesException(userId, workplaceId)
        val engagement = sensitive.getOrThrow()
            .financialEngagements[workplaceId] ?: throw NoPayRatesException(userId, workplaceId)
        val theirHours = shiftRepository.getShiftsForRoster(shift.rosterId).getOrDefault(emptyList())
            .filter { it.workerId == userId }
            .sumOf { hoursOf(it.plannedTime.start, it.plannedTime.end) }
        financialRepository.updateShiftFinancials(price(shift, userId, engagement, settings, theirHours)).getOrThrow()
    }

    /**
     * Prices the roster's shifts whose snapshot is missing or stale and removes the snapshots of
     * shifts nobody works. A snapshot is history: it is kept unless the shift changed hands, has
     * unpublished edits, was clocked out since, or is on/after [repriceFrom] (pay rates changed).
     * Shifts of people without rates for the workplace are skipped. Returns how many were priced.
     *
     * SH-260: everyone's pay terms are read at the same time and the snapshots written and removed
     * in batched writes ([FinancialRepository.writeShiftFinancials]): one write per snapshot, each
     * awaited in turn, took minutes for a month imported at once.
     */
    suspend fun refreshRoster(rosterId: String, repriceFrom: String? = null): Result<Int> = runCatching {
        if (!caller.allows(RosterIds.workplaceIdOf(rosterId), Capability.MANAGE_PAY)) return@runCatching 0
        // SH-267: borrowed people's shifts (and their snapshots) are the server's: neither priced nor removed here.
        val all = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
        val serverPriced = all.filter { it.pricedOnServer }.mapTo(HashSet()) { it.id }
        val shifts = all.filter { it.id !in serverPriced }
        val snapshots = financialRepository.getShiftFinancialsForRoster(rosterId).getOrThrow()
            .filter { it.shiftId !in serverPriced }.associateBy { it.shiftId }
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
        val settings = workplace.settings

        // An unpaid owner's shifts are not paid work: their snapshots go like an empty shift's.
        val worked = shifts.filter { shift -> shift.workerId?.let { workplace.isPaid(it, shift.date.ifEmpty { shift.plannedTime.start }) } == true }.associateBy { it.id }
        val orphans = snapshots.keys.filter { it !in worked }

        val hoursByUser = worked.values.groupBy { it.workerId!! }
            .mapValues { (_, theirs) -> theirs.sumOf { hoursOf(it.plannedTime.start, it.plannedTime.end) } }
        val stale = worked.values.filter { shift ->
            val snapshot = snapshots[shift.id]
            val clockedOut = shift.actualTime?.let { it.start.isNotEmpty() && it.end.isNotEmpty() } == true
            snapshot == null || snapshot.userId != shift.workerId || shift.hasUnpublishedChanges ||
                (repriceFrom != null && shift.date >= repriceFrom) ||
                (clockedOut && snapshot.costs.actualEmployerCost == null)
        }
        // A person's terms are read once, everyone's at the same time.
        val engagements: Map<String, FinancialEngagement?> = coroutineScope {
            stale.map { it.workerId!! }.distinct().map { userId ->
                async { userId to userRepository.getSensitiveUserData(userId).getOrNull()?.financialEngagements?.get(workplaceId) }
            }.awaitAll().toMap()
        }
        val priced = stale.mapNotNull { shift ->
            val userId = shift.workerId!!
            val engagement = engagements[userId] ?: return@mapNotNull null
            price(shift, userId, engagement, settings, hoursByUser.getValue(userId))
        }
        if (priced.isNotEmpty() || orphans.isNotEmpty()) financialRepository.writeShiftFinancials(priced, orphans).getOrThrow()
        priced.size
    }

    companion object {
        /** Pure pricing; [rosterHours] = the person's planned hours this month (for monthly thresholds). */
        fun price(
            shift: Shift,
            userId: String,
            engagement: FinancialEngagement,
            settings: WorkplaceSettings,
            rosterHours: Double
        ): ShiftFinancials {
            val rules = PayrollRules.of(settings.market)
            // SH-57: the terms in force on the shift's day (a rate change applies from its date).
            val day = shift.date.ifEmpty { shift.plannedTime.start }
            val pay = engagement.compensationOn(day)
            // SH-149: and the payroll facts of that day (a status that started, or ended on its known day).
            val profile = engagement.profileOn(day)
            // SH-212: and the contract of that day (a change of contract applies from its date).
            val contract = engagement.contractOn(day)
            val expectedMonth = when (pay) {
                is CompensationModel.Hourly -> pay.rate * (contract.declaredNumberOfHours?.toDouble() ?: rosterHours)
                is CompensationModel.MonthlyFixed -> pay.baseSalary
            }
            fun priced(start: String, end: String, unpaidBreaks: List<WorkedSpan> = emptyList()) = rules.shiftPay(
                ShiftPayInput(
                    contract = contract.contract,
                    compensation = pay,
                    profile = profile,
                    start = LocalDateTime.parse(start),
                    end = LocalDateTime.parse(end),
                    settings = settings.payroll,
                    expectedMonthlyGross = expectedMonth.takeIf { it > 0 },
                    unpaidBreaks = unpaidBreaks
                )
            )
            val planned = priced(shift.plannedTime.start, shift.plannedTime.end)
            // SH-134: the payable times (from the planned start where the workplace says so, unless approved).
            val actual = shift.payableTimes(settings.clockReview)?.let {
                if (it.end > it.start) priced(it.start, it.end, shift.unpaidBreaks(settings.breakPolicy, settings.clockReview)) else null
            }
            val clampedToNothing = actual == null && shift.actualTime?.let { it.start.isNotEmpty() && it.end.isNotEmpty() } == true
            return ShiftFinancials(
                shiftId = shift.id,
                rosterId = shift.rosterId,
                dailyRecordId = shift.dailyRecordId,
                date = shift.date,
                userId = userId,
                ratesSnapshot = RatesSnapshot(
                    rateUsed = planned.hourlyRate,
                    employerRateUsed = if (planned.hours > 0) planned.employerCost / planned.hours else 0.0,
                    contractType = contract.contract.name,
                    currency = settings.currency,
                    market = settings.market.id,
                    plannedHours = planned.hours,
                    nightHours = planned.nightHours
                ),
                costs = ShiftCostBreakdown(
                    estimatedPayout = planned.gross,
                    employerCost = planned.employerCost,
                    actualPayout = actual?.gross ?: if (clampedToNothing) 0.0 else planned.gross,
                    nightAllowance = planned.nightAllowance,
                    employerContributions = planned.employerContributions,
                    actualEmployerCost = actual?.employerCost ?: if (clampedToNothing) 0.0 else null
                )
            )
        }

        internal fun hoursOf(start: String, end: String): Double = runCatching {
            val s = LocalDateTime.parse(start)
            val e = LocalDateTime.parse(end)
            (e.hour * 60 + e.minute - s.hour * 60 - s.minute + if (e.date > s.date) 24 * 60 else 0) / 60.0
        }.getOrDefault(0.0)
    }
}
